<template>
  <div class="container">

    <h1 class="se-bp-title">Pay Details: {{ getWageEmploymentLabel(employmentIdx) }}</h1>

    <template v-if="currentEmployment">
      <el-collapse v-model="activePanels" >
        <el-collapse-item v-for="(rate, idx) in currentEmployment.payRates" :key="idx" :name="idx">
          <template #title>
            <div class="se-earnings-header-row">
              <div class="se-earnings-title-block">
                <div class="se-panel-title">{{ rateTitle(rate) }}</div>
                <template v-if="isBasePay(rate) || isBonusOrCommission(rate)">
                  <table class="se-paystub-summary-table" v-if="paystubSummariesFor(rate).length > 0" @click.stop>
                    <thead>
                      <tr>
                        <th>Paystub</th>
                        <th>Date Paid</th>
                        <th>Pay Period</th>
                        <th>Year-to-Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(summary, sIdx) in paystubSummariesFor(rate)" :key="sIdx">
                        <td>{{ summary.typeLabel }}</td>
                        <td>{{ formatDate(summary.payDate) }}</td>
                        <td>{{ formatCurrency(summary.payPeriodAmount) }}</td>
                        <td>{{ formatCurrency(summary.payTotalAmount) }}</td>
                      </tr>
                    </tbody>
                  </table>
                  <div class="se-panel-subtitle" v-else>No paystub data entered yet</div>
                </template>
                <div class="se-panel-subtitle" v-else>{{ rateSubtitle(rate) }}</div>
              </div>
              <button v-if="!isDefaultCategoryRate(idx)" class="bp-button bp-button--destructive bp-button--small se-earnings-delete-btn" :disabled="isLocked" @click.stop.prevent="removePayRate(idx)">Delete</button>
              <span class="se-earnings-status" :class="isRateComplete(rate) ? 'se-earnings-status--complete' : 'se-earnings-status--incomplete'">
                {{ isRateComplete(rate) ? 'Complete' : 'Incomplete' }}
              </span>
            </div>
          </template>

          <div class="row mt-2">
            <div class="col-md-2 mt-2">
              <label class="bp-label">Category</label>
              <el-select size="large" :class="{'se-readonly-select': isDefaultCategoryRate(idx)}" :model-value="rate.category" @update:model-value="v => setRateField(idx, 'category', v)" :disabled="isLocked || isDefaultCategoryRate(idx)" clearable placeholder="Select">
                <el-option v-for="opt in categoryOptionsFor(idx)" :key="opt.value" :label="opt.label" :value="opt.value"/>
              </el-select>
            </div>
            <div class="col-md-4 mt-2" v-if="!isBasePay(rate) && !isBonusOrCommission(rate)">
              <label class="bp-label">{{ mostRecentPaymentDateLabel(rate) }}</label>
              <el-date-picker size="large" :model-value="rate.mostRecentPaymentDate" @update:model-value="v => setRateField(idx, 'mostRecentPaymentDate', v)" type="date" value-format="MM/DD/YYYY" format="MM/DD/YYYY" :disabled="isLocked" clearable placeholder="Select date"/>
            </div>
          </div>

          <div class="row mt-2" v-if="isBonusOrCommission(rate)">
            <div class="col-md-5 mt-2">
              <label class="bp-label se-nowrap-label">{{ mostRecentPaymentDateLabel(rate) }}</label>
              <el-date-picker size="large" class="se-short-field" :model-value="rate.mostRecentPaymentDate" @update:model-value="v => setRateField(idx, 'mostRecentPaymentDate', v)" type="date" value-format="MM/DD/YYYY" format="MM/DD/YYYY" :disabled="isLocked" clearable placeholder="Select date"/>
            </div>
          </div>

          <!-- ================= Base Pay ================= -->
          <template v-if="isBasePay(rate)">
            <div class="row mt-2">
              <div class="col-md-2 mt-2">
                <label class="bp-label">Rate of Pay
                  <a class="btn-link se-help-icon" v-tooltip.top="'Learn More'" @click="showRateOfPayHelp = true">
                    <i class="fa fa-info-circle"></i>
                  </a>
                </label>
                <input class="bp-form-input bp-u-shadow-04" type="text" :readonly="isLocked" :disabled="isBaseRateDisabled(idx)" v-mask="currencyMask" :value="rate.rateOfPay" @change="setRateField(idx, 'rateOfPay', parseCurrencyToNumber($event.target.value))"/>
              </div>
              <div class="col-md-2 mt-2">
                <label class="bp-label">Base Pay Basis</label>
                <el-select size="large" :model-value="rate.payFrequency" @update:model-value="v => setRateField(idx, 'payFrequency', v)" :disabled="isLocked || isBaseRateDisabled(idx)" clearable placeholder="Select">
                  <el-option v-for="opt in basePayBasisOptions" :key="opt.value" :label="opt.label" :value="opt.value"/>
                </el-select>
              </div>
            </div>

            <div class="row mt-2" v-if="rate.payFrequency === 'hourly'">
              <div class="col-md-8 mt-2">
                <label class="bp-label se-nowrap-label">Are the borrower&rsquo;s hours fixed or variable per pay period?</label>
                <div style="width: 200px; max-width: 100%;">
                  <el-select size="large" style="width: 100%;" :model-value="rate.basisType" @update:model-value="v => setRateField(idx, 'basisType', v)" :disabled="isLocked || isBaseRateDisabled(idx)" clearable placeholder="Select">
                    <el-option v-for="opt in fixedVariableOptions" :key="opt.value" :label="opt.label" :value="opt.value"/>
                  </el-select>
                </div>
              </div>
            </div>

            <div class="row mt-2" v-if="rate.payFrequency === 'hourly'">
              <div class="col-md-4 mt-2">
                <label class="bp-label">Average hours per pay period</label>
                <div style="width: 200px; max-width: 100%;">
                  <fg-input type="number" :readonly="isLocked" :disabled="isBaseRateDisabled(idx)" :model-value="rate.averageHoursPerCycle" @update:model-value="v => setRateField(idx, 'averageHoursPerCycle', Number(v))"></fg-input>
                </div>
              </div>
            </div>

            <div class="row mt-3">
              <div class="col-md-12 mt-2">
                <label class="bp-label d-block">How often is the borrower paid?</label>
                <radio :label="'cycle'" :disabled="isLocked || isBaseRateDisabled(idx)" :model-value="paymentCycleMode(idx)" @update:model-value="v => setPaymentCycleMode(idx, v)" class="d-inline-block mr-4">Payment Cycle</radio>
                <radio :label="'timesPerYear'" :disabled="isLocked || isBaseRateDisabled(idx)" :model-value="paymentCycleMode(idx)" @update:model-value="v => setPaymentCycleMode(idx, v)" class="d-inline-block">Payment Cycle Times Per Year</radio>
              </div>
            </div>

            <div class="row mt-2">
              <div class="col-md-2 mt-2" v-if="paymentCycleMode(idx) === 'cycle'">
                <label class="bp-label">Payment Cycle</label>
                <el-select size="large" :model-value="rate.paymentCycle" @update:model-value="v => setRateField(idx, 'paymentCycle', v)" :disabled="isLocked || isBaseRateDisabled(idx)" clearable placeholder="Select">
                  <el-option v-for="opt in basePayCycleOptions" :key="opt.value" :label="opt.label" :value="opt.value"/>
                </el-select>
              </div>
              <div class="col-md-3 mt-2" v-else>
                <label class="bp-label">Payment Cycle Times Per Year</label>
                <fg-input type="number" :readonly="isLocked" :disabled="isBaseRateDisabled(idx)" :model-value="rate.paymentCycleTimesPerYear" @update:model-value="v => setRateField(idx, 'paymentCycleTimesPerYear', Number(v))"></fg-input>
              </div>
            </div>

            <div class="row mt-3">
              <div class="col-md-6 mt-2">
                <label class="bp-label d-block">Is the borrower paid less than 12 months per year?</label>
                <radio :label="'yes'" :disabled="isLocked" :model-value="lessThan12MonthsValue(idx)" @update:model-value="v => onLessThan12MonthsChange(idx, v)" class="d-inline-block mr-4">Yes</radio>
                <radio :label="'no'" :disabled="isLocked" :model-value="lessThan12MonthsValue(idx)" @update:model-value="v => onLessThan12MonthsChange(idx, v)" class="d-inline-block">No</radio>
              </div>
            </div>
          </template>

          <!-- ================= Bonus / Commission ================= -->
          <template v-else-if="isBonusOrCommission(rate)">
            <div class="row mt-3">
              <div class="col-md-12 mt-2">
                <label class="bp-label d-block">{{ howOftenPaidLabel(rate) }}</label>
                <radio :label="'cycle'" :disabled="isLocked" :model-value="paymentCycleMode(idx)" @update:model-value="v => setPaymentCycleMode(idx, v)" class="d-inline-block mr-4">Payment Cycle</radio>
                <radio :label="'timesPerYear'" :disabled="isLocked" :model-value="paymentCycleMode(idx)" @update:model-value="v => setPaymentCycleMode(idx, v)" class="d-inline-block">Payment Cycle Times Per Year</radio>
              </div>
            </div>

            <div class="row mt-2">
              <div class="col-md-2 mt-2" v-if="paymentCycleMode(idx) === 'cycle'">
                <label class="bp-label">Payment Cycle</label>
                <el-select size="large" :model-value="rate.paymentCycle" @update:model-value="v => setRateField(idx, 'paymentCycle', v)" :disabled="isLocked" clearable placeholder="Select">
                  <el-option v-for="opt in payCycleOptions" :key="opt.value" :label="opt.label" :value="opt.value"/>
                </el-select>
              </div>
              <div class="col-md-4 mt-2" v-else>
                <label class="bp-label">Payment Cycle Times Per Year</label>
                <fg-input type="number" :readonly="isLocked" :model-value="rate.paymentCycleTimesPerYear" @update:model-value="v => setRateField(idx, 'paymentCycleTimesPerYear', Number(v))"></fg-input>
              </div>
            </div>
          </template>

          <!-- ================= Other (all remaining, non-Base-Pay/Bonus/Commission categories) ================= -->
          <template v-else>
            <div class="row mt-2">
              <div class="col-md-4 mt-3">
                <div class="se-outlined-box">
                  <div class="se-outlined-field">
                    <label class="bp-label">Rate of Pay
                      <a class="btn-link se-help-icon" v-tooltip.top="'Learn More'" @click="showRateOfPayHelp = true">
                        <i class="fa fa-info-circle"></i>
                      </a>
                    </label>
                    <input class="bp-form-input bp-u-shadow-04" type="text" :readonly="isLocked" v-mask="currencyMask" :value="rate.rateOfPay" @change="setRateField(idx, 'rateOfPay', parseCurrencyToNumber($event.target.value))"/>
                  </div>
                  <div class="se-outlined-field">
                    <label class="bp-label">Pay Frequency</label>
                    <el-select size="large" :model-value="rate.payFrequency" @update:model-value="v => setRateField(idx, 'payFrequency', v)" :disabled="isLocked" clearable placeholder="Select">
                      <el-option v-for="opt in payFrequencyOptions" :key="opt.value" :label="opt.label" :value="opt.value"/>
                    </el-select>
                  </div>
                  <div class="se-outlined-field">
                    <label class="bp-label">Pay Frequency Times Per Year
                      <a class="btn-link se-help-icon" v-tooltip.top="'Learn More'" @click="showPayFrequencyTimesPerYearHelp = true">
                        <i class="fa fa-info-circle"></i>
                      </a>
                    </label>
                    <fg-input type="number" :readonly="isLocked" :model-value="rate.payFrequencyTimesPerYear" @update:model-value="v => setRateField(idx, 'payFrequencyTimesPerYear', Number(v))"></fg-input>
                  </div>
                </div>
              </div>

              <div class="col-md-6 mt-3">
                <div class="se-outlined-box">
                  <div class="se-outlined-field">
                    <label class="bp-label">Payment Cycle</label>
                    <el-select size="large" :model-value="rate.paymentCycle" @update:model-value="v => setRateField(idx, 'paymentCycle', v)" :disabled="isLocked" clearable placeholder="Select">
                      <el-option v-for="opt in payCycleOptions" :key="opt.value" :label="opt.label" :value="opt.value"/>
                    </el-select>
                  </div>
                  <div class="se-outlined-field">
                    <label class="bp-label">Payment Cycle Times Per Year
                      <a class="btn-link se-help-icon" v-tooltip.top="'Learn More'" @click="showPaymentCycleTimesPerYearHelp = true">
                        <i class="fa fa-info-circle"></i>
                      </a>
                    </label>
                    <fg-input type="number" :readonly="isLocked" :model-value="rate.paymentCycleTimesPerYear" @update:model-value="v => setRateField(idx, 'paymentCycleTimesPerYear', Number(v))"></fg-input>
                  </div>
                  <div class="se-outlined-field">
                    <label class="bp-label">Average Hours Per Cycle
                      <a class="btn-link se-help-icon" v-tooltip.top="'Learn More'" @click="showAverageHoursPerCycleHelp = true">
                        <i class="fa fa-info-circle"></i>
                      </a>
                    </label>
                    <fg-input type="number" :readonly="isLocked" :model-value="rate.averageHoursPerCycle" @update:model-value="v => setRateField(idx, 'averageHoursPerCycle', Number(v))"></fg-input>
                  </div>
                </div>
              </div>
            </div>

            <div class="row mt-3">
              <div class="col-md-4 mt-3">
                <el-checkbox :model-value="rate.variableIncomeDecreaseExpectedIndicator" @update:model-value="v => setRateField(idx, 'variableIncomeDecreaseExpectedIndicator', v)" :disabled="isLocked">Variable Income Decrease Expected</el-checkbox>
              </div>
              <div class="col-md-4 mt-3">
                <el-checkbox :model-value="rate.incomeLikelyToContinue" @update:model-value="v => setRateField(idx, 'incomeLikelyToContinue', v)" :disabled="isLocked">Income Likely to Continue</el-checkbox>
              </div>
            </div>
          </template>

        </el-collapse-item>
      </el-collapse>

      <se-help v-model:visible="showRateOfPayHelp" title="Learn More">
        <p>The rate of base income earned at the corresponding pay frequency</p>
      </se-help>

      <se-help v-model:visible="showPayFrequencyTimesPerYearHelp" title="Learn More">
        <p>Generally will be equal to PayCycle TimesPerYear count, except for academic or seasonal employment where the borrower consistently earns income fewer than 12 months in the year but may request to have the employer pay out their income over a different pay schedule, such as over 12 months.</p>
      </se-help>

      <se-help v-model:visible="showPaymentCycleTimesPerYearHelp" title="Learn More">
        <p>How many times in a calendar year the borrower receives income payments from the employer</p>
      </se-help>

      <se-help v-model:visible="showAverageHoursPerCycleHelp" title="Learn More">
        <p>The average number of hours worked per payment cycle for this category. Used with Payment Cycle and Rate Of Pay to calculate hourly-basis income. For example, if paymentCycle is "biweekly" and Average Hours Per Cycle Count is 85, the annualized amount is Rate Of Pay &times; 85 &times; 26 / 12</p>
      </se-help>

<!--      <div class="se-earnings-add-row">-->
<!--        <button class="bp-button bp-button&#45;&#45;primary" :disabled="isLocked" @click.prevent="addPayRate">Add Pay Rate</button>-->
<!--      </div>-->
    </template>

    <se-buttons
      :display-previous="true"
      :display-save="false"
      :display-submit="false"
      @seNext="next"
      @sePrevious="previous">
    </se-buttons>

  </div>
</template>
<script>
import { defineComponent } from 'vue';
import {mapGetters} from 'vuex'
import {ElSelect, ElOption, ElDatePicker, ElCheckbox, ElCollapse, ElCollapseItem} from 'element-plus'
import {Radio} from 'src/components/index'
import Swal from 'sweetalert2/dist/sweetalert2'
import SeButtons from './SeButtons.vue'
import SeHelp from './SeHelp.vue'
import wageFormMixin from '@/mixins/WageFormMixin'
import Enums from 'src/model/enums'
import {
  ADD_WAGE_PAY_RATE, SET_WAGE_PAY_RATE, DELETE_WAGE_PAY_RATE
} from '@/store/actions/se'

function toOptions(valuesEnum, labelsEnum) {
  return Object.keys(valuesEnum).map(key => ({value: valuesEnum[key], label: labelsEnum[key]}))
}

export default defineComponent({
  mixins: [wageFormMixin],

  components: {
    SeButtons,
    SeHelp,
    Radio,
    [ElSelect.name]: ElSelect,
    [ElOption.name]: ElOption,
    [ElDatePicker.name]: ElDatePicker,
    [ElCheckbox.name]: ElCheckbox,
    [ElCollapse.name]: ElCollapse,
    [ElCollapseItem.name]: ElCollapseItem
  },

  data() {
    return {
      activePanels: null,

      showRateOfPayHelp: false,
      showPayFrequencyTimesPerYearHelp: false,
      showPaymentCycleTimesPerYearHelp: false,
      showAverageHoursPerCycleHelp: false,

      payFrequencyOptions: toOptions(Enums.SeEmployeePayFrequencyType, Enums.SeEmployeePayFrequencyTypeLabel),
      payCycleOptions: toOptions(Enums.SeEmployeePayCycleType, Enums.SeEmployeePayCycleTypeLabel),
      categoryOptions: toOptions(Enums.SeWageIncomeItemCategoryType, Enums.SeWageIncomeItemCategoryTypeLabel),

      // Base Pay Basis is restricted to these frequencies only.
      basePayBasisOptions: toOptions(Enums.SeEmployeePayFrequencyType, Enums.SeEmployeePayFrequencyTypeLabel)
        .filter(opt => ['hourly', 'daily', 'weekly', 'biweekly', 'semimonthly', 'monthly', 'annual'].includes(opt.value)),

      // Base Pay's "Payment Cycle" is restricted to these cycles only.
      basePayCycleOptions: toOptions(Enums.SeEmployeePayCycleType, Enums.SeEmployeePayCycleTypeLabel)
        .filter(opt => ['daily', 'weekly', 'biweekly', 'semimonthly', 'monthly', 'annual'].includes(opt.value)),

      // Base Pay/Bonus/Commissions are always auto-created (see ensureDefaultPayRates) and their
      // Category is read-only. Any additional pay rate added via "Add Pay Rate" gets an editable
      // Category limited to the remaining (non-default) categories, since those three already exist.
      nonDefaultCategoryOptions: toOptions(Enums.SeWageIncomeItemCategoryType, Enums.SeWageIncomeItemCategoryTypeLabel)
        .filter(opt => opt.value !== Enums.SeWageIncomeItemCategoryType.base
          && opt.value !== Enums.SeWageIncomeItemCategoryType.bonus
          && opt.value !== Enums.SeWageIncomeItemCategoryType.commissions),

      // Base Pay only allows Fixed/Variable (not "Other") for "Are the borrower's hours
      // fixed or variable per pay period?" (still maps to payRates/basisType).
      fixedVariableOptions: [
        {value: Enums.SeEmployeeBasisType.fixed, label: Enums.SeEmployeeBasisTypeLabel.fixed},
        {value: Enums.SeEmployeeBasisType.variable, label: Enums.SeEmployeeBasisTypeLabel.variable}
      ],

      // Transient, per-rate UI-only state (not part of the API model): which of the two
      // mutually-exclusive fields ("Payment Cycle" vs "Payment Cycle Times Per Year") is
      // currently shown for Base Pay, and whether "paid less than 12 months per year" is
      // set (which disables/clears the whole Base Pay category when Yes).
      paymentCycleModeByIdx: {},
      lessThan12MonthsByIdx: {}
    }
  },


  created() {
    this.ensureDefaultPayRates()
  },

  computed: {
    ...mapGetters(['getWageEmploymentLabel'])
  },

  watch: {
    '$route.params'() {
      this.activePanels = null
      this.ensureDefaultPayRates()
    }
  },

  methods: {
    ensureDefaultPayRates() {
      if (this.currentEmployment && (this.currentEmployment.payRates || []).length === 0) {
        [Enums.SeWageIncomeItemCategoryType.base,
         Enums.SeWageIncomeItemCategoryType.bonus,
         Enums.SeWageIncomeItemCategoryType.commissions].forEach(category => {
          this.$store.commit(ADD_WAGE_PAY_RATE, {employmentIdx: this.employmentIdx})
          let newIdx = this.currentEmployment.payRates.length - 1
          this.setRateField(newIdx, 'category', category)
        })
        this.saveRequest()
      }
      this.expandBasePayPanel()
    },

    expandBasePayPanel() {
      let basePayIdx = (this.currentEmployment?.payRates || []).findIndex(this.isBasePay)
      if (basePayIdx >= 0) {
        this.activePanels = [basePayIdx]
      }
    },

    categoryLabel(v) { return Enums.SeWageIncomeItemCategoryTypeLabel[v] || v },
    payFrequencyLabel(v) { return Enums.SeEmployeePayFrequencyTypeLabel[v] || v },
    payCycleLabel(v) { return Enums.SeEmployeePayCycleTypeLabel[v] || v },
    basisTypeLabel(v) { return Enums.SeEmployeeBasisTypeLabel[v] || v },

    // Base Pay/Bonus/Commissions are the three always-present default categories (auto-created by
    // ensureDefaultPayRates); their Category is read-only. Any other pay rate (added via "Add Pay
    // Rate") gets an editable Category limited to the remaining categories.
    isDefaultCategoryRate(idx) {
      let category = this.currentEmployment?.payRates?.[idx]?.category
      return category === Enums.SeWageIncomeItemCategoryType.base
        || category === Enums.SeWageIncomeItemCategoryType.bonus
        || category === Enums.SeWageIncomeItemCategoryType.commissions
    },

    categoryOptionsFor(idx) {
      return this.isDefaultCategoryRate(idx) ? this.categoryOptions : this.nonDefaultCategoryOptions
    },

    formatCurrency(v) {
      if (v === null || v === undefined || v === '') return '0.00'
      return Number(v).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})
    },

    formatDate(v) {
      return v || 'N/A'
    },

    paystubTypeLabel(wageDocumentation) {
      if (wageDocumentation === Enums.SeWageDocumentationType.yearToDatePayStub) return 'YTD Paystub'
      if (wageDocumentation === Enums.SeWageDocumentationType.yearEndPayStub) return 'Prior Year Paystub'
      return 'Paystub'
    },

    // For Base Pay/Bonus/Commission panels, surface the underlying paystub data (entered on the
    // Input Data pages) that corresponds to this rate's category, rather than the rate fields
    // themselves, since those categories are driven by what's reported on the borrower's paystubs.
    paystubSummariesFor(rate) {
      let docs = this.currentEmployment?.incomeDocuments || []
      let summaries = []
      docs.forEach(doc => {
        if (doc.wageDocumentation !== Enums.SeWageDocumentationType.yearToDatePayStub
          && doc.wageDocumentation !== Enums.SeWageDocumentationType.yearEndPayStub) {
          return
        }
        (doc.incomeItems || []).forEach(item => {
          if (item.category === rate.category) {
            summaries.push({
              typeLabel: this.paystubTypeLabel(doc.wageDocumentation),
              payDate: doc.payDate,
              payPeriodAmount: item.payPeriodAmount,
              payTotalAmount: item.payTotalAmount
            })
          }
        })
      })
      return summaries
    },

    setRateField(idx, key, value) {
      this.$store.commit(SET_WAGE_PAY_RATE, {employmentIdx: this.employmentIdx, rateIdx: idx, fields: {[key]: value}})
    },

    // ---- Base Pay: "How often is the borrower paid?" Payment Cycle vs Payment Cycle Times Per Year ----
    paymentCycleMode(idx) {
      if (this.paymentCycleModeByIdx[idx]) return this.paymentCycleModeByIdx[idx]
      let rate = this.currentEmployment?.payRates?.[idx]
      return (rate && rate.paymentCycleTimesPerYear !== null && rate.paymentCycleTimesPerYear !== undefined) ? 'timesPerYear' : 'cycle'
    },

    setPaymentCycleMode(idx, mode) {
      this.paymentCycleModeByIdx[idx] = mode
      // Clear whichever field is no longer shown, so we never submit stale data for both.
      if (mode === 'cycle') {
        this.setRateField(idx, 'paymentCycleTimesPerYear', null)
      } else {
        this.setRateField(idx, 'paymentCycle', null)
      }
    },

    // ---- Base Pay: "Is the borrower paid less than 12 months per year?" ----
    // Not part of the API model - UI-only gate used to block/clear unsupported Base Pay data.
    lessThan12MonthsValue(idx) {
      return this.lessThan12MonthsByIdx[idx] || 'no'
    },

    isBaseRateDisabled(idx) {
      return this.lessThan12MonthsByIdx[idx] === 'yes'
    },

    onLessThan12MonthsChange(idx, value) {
      this.lessThan12MonthsByIdx[idx] = value
      if (value === 'yes') {
        Swal.fire({
          html: '<p>Income Calculator does not currently support income amounts received less than 12 months</p>',
          width: 480,
          buttonsStyling: false,
          customClass: {confirmButton: 'bp-button bp-button--primary'},
          confirmButtonText: 'OK',
          icon: 'warning'
        })
        this.clearBasePayFields(idx)
      }
    },

    clearBasePayFields(idx) {
      this.setRateField(idx, 'rateOfPay', null)
      this.setRateField(idx, 'payFrequency', null)
      this.setRateField(idx, 'basisType', null)
      this.setRateField(idx, 'averageHoursPerCycle', null)
      this.setRateField(idx, 'paymentCycle', null)
      this.setRateField(idx, 'paymentCycleTimesPerYear', null)
      this.setRateField(idx, 'variableIncomeDecreaseExpectedIndicator', null)
      this.setRateField(idx, 'incomeLikelyToContinue', null)
      this.setRateField(idx, 'mostRecentPaymentDate', null)
      this.paymentCycleModeByIdx[idx] = 'cycle'
    },

    addPayRate() {
      this.$store.commit(ADD_WAGE_PAY_RATE, {employmentIdx: this.employmentIdx})
      let newIdx = this.currentEmployment.payRates.length - 1
      this.activePanels = newIdx
      this.saveRequest()
    },

    removePayRate(idx) {
      this.confirmDelete('This pay rate will be permanently removed.', () => {
        this.$store.commit(DELETE_WAGE_PAY_RATE, {employmentIdx: this.employmentIdx, rateIdx: idx})
        this.saveRequest()
      })
    },

    rateTitle(rate) {
      if (!rate.category) return 'New Pay Rate'
      let title = this.categoryLabel(rate.category)
      if (this.isBasePay(rate) && rate.basisType) {
        title += ' - ' + this.basisTypeLabel(rate.basisType)
      }
      return title
    },

    rateSubtitle(rate) {
      let parts = []
      if (this.isBonusOrCommission(rate)) {
        if (rate.paymentCycle) {
          parts.push(this.payCycleLabel(rate.paymentCycle))
        }
        if (rate.paymentCycleTimesPerYear !== null && rate.paymentCycleTimesPerYear !== undefined) {
          parts.push(rate.paymentCycleTimesPerYear + ' times per year')
        }
        return parts.join(' | ')
      }
      if (rate.rateOfPay !== null && rate.rateOfPay !== undefined) {
        parts.push(this.formatCurrency(rate.rateOfPay))
      }
      if (rate.payFrequency) {
        parts.push(this.payFrequencyLabel(rate.payFrequency))
      }
      if (rate.paymentCycle) {
        parts.push(this.payCycleLabel(rate.paymentCycle))
      }
      if (rate.variableIncomeDecreaseExpectedIndicator) {
        parts.push('Variable Income Decrease Expected')
      }
      if (rate.incomeLikelyToContinue) {
        parts.push('Income Likely to Continue')
      }
      return parts.join(' | ')
    },

    isRateComplete(rate) {
      if (!rate.category) return false
      if (this.isBonusOrCommission(rate)) {
        return !!(rate.paymentCycle
          || (rate.paymentCycleTimesPerYear !== null && rate.paymentCycleTimesPerYear !== undefined))
      }
      return !!(rate.rateOfPay !== null && rate.rateOfPay !== undefined
        && rate.payFrequency && rate.paymentCycle)
    },

    isBasePay(rate) {
      return rate.category === Enums.SeWageIncomeItemCategoryType.base
    },

    isBonusOrCommission(rate) {
      return rate.category === Enums.SeWageIncomeItemCategoryType.bonus
        || rate.category === Enums.SeWageIncomeItemCategoryType.commissions
    },


    mostRecentPaymentDateLabel(rate) {
      if (rate.category === Enums.SeWageIncomeItemCategoryType.bonus) {
        return 'Date bonus was last paid, if available'
      }
      if (rate.category === Enums.SeWageIncomeItemCategoryType.commissions) {
        return 'Date commission was last paid, if available'
      }
      return 'Most Recent Payment Date'
    },

    howOftenPaidLabel(rate) {
      if (rate.category === Enums.SeWageIncomeItemCategoryType.bonus) {
        return 'How often is the bonus paid?'
      }
      if (rate.category === Enums.SeWageIncomeItemCategoryType.commissions) {
        return 'How often is the commission paid?'
      }
      return 'How often is the borrower paid?'
    }
  },
});
</script>
<style scoped>
.se-outlined-box {
  border: 1px solid rgba(0, 0, 0, .15);
  border-radius: 4px;
  padding: 12px 16px;
}

.se-readonly-select :deep(.el-select__wrapper) {
  background-color: #ebebeb !important;
  cursor: not-allowed;
}

.se-readonly-select :deep(.el-select__selection),
.se-readonly-select :deep(.el-select__placeholder) {
  color: rgba(0, 0, 0, .6) !important;
}

.se-outlined-field {
  display: flex;
  flex-direction: column;
}

.se-outlined-field + .se-outlined-field {
  margin-top: 10px;
}

.se-outlined-field :deep(.bp-form-item) {
  margin-bottom: 0;
}


:deep(.bp-label) {
  display: flex;
  align-items: flex-end;
  min-height: 34px;
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
}

:deep(.form-check-radio),
:deep(.form-check-label) {
  font-size: 1rem !important;
}

:deep(.form-check-radio) {
  padding-left: 0 !important;
}

:deep(.form-check-label) {
  display: inline-flex !important;
  align-items: center !important;
  padding-left: 0 !important;
  gap: 8px;
  cursor: pointer;
}

:deep(.form-check-radio input[type="radio"]) {
  position: absolute !important;
  opacity: 0 !important;
  visibility: hidden !important;
  pointer-events: none;
  width: 0 !important;
  height: 0 !important;
}

:deep(.form-check-sign) {
  position: relative !important;
  display: inline-block !important;
  flex: 0 0 18px !important;
  width: 18px !important;
  height: 18px !important;
  margin: 0 !important;
  border-radius: 50% !important;
  border: 2px solid rgba(0, 0, 0, .4) !important;
  box-sizing: border-box !important;
  background: #fff !important;
}

:deep(.form-check-sign::before) {
  content: '' !important;
  display: none !important;
}

:deep(.form-check-radio input[type="radio"] + .form-check-sign::after),
:deep(.form-check-sign::after) {
  content: '' !important;
  font-family: initial !important;
  position: absolute !important;
  top: 50% !important;
  left: 50% !important;
  width: 8px !important;
  height: 8px !important;
  margin: 0 !important;
  border-radius: 50% !important;
  background: #1887cc !important;
  opacity: 1 !important;
  transform: translate(-50%, -50%) scale(0) !important;
  transition: transform .15s ease !important;
}

:deep(.form-check-radio input[type="radio"]:checked + .form-check-sign) {
  border-color: #1887cc !important;
}

:deep(.form-check-radio input[type="radio"]:checked + .form-check-sign::after) {
  transform: translate(-50%, -50%) scale(1) !important;
}


:deep(.bp-form-item) > .bp-form-label {
  display: flex;
  align-items: flex-end;
  min-height: 34px;
  margin: 0 0 12px 0;
}

:deep(.el-select),
:deep(.el-date-editor.el-input) {
  width: 100%;
}

.se-short-field:deep(.el-input__wrapper),
:deep(.el-date-editor.se-short-field) {
  max-width: 180px;
}

.se-nowrap-label {
  white-space: nowrap;
}

.se-medium-field,
:deep(.el-select.se-medium-field) {
  width: 200px !important;
  max-width: 200px;
}

:deep(.el-select__wrapper),
:deep(.el-input__wrapper) {
  min-height: 42px;
  padding: 8px 12px;
  border-radius: 4px;
  box-shadow: none !important;
  border: 1px solid rgba(0, 0, 0, .4);
  background-color: #fff;
}

:deep(.el-select__wrapper.is-hovering:not(.is-focused)),
:deep(.el-input__wrapper:hover) {
  border-color: rgba(0, 0, 0, .4);
}

:deep(.el-select__wrapper.is-focused),
:deep(.el-input__wrapper.is-focus) {
  border-color: #1887cc;
  box-shadow: 0 0 0 1px rgba(24, 135, 204, .4) !important;
}

:deep(.el-select__selection),
:deep(.el-input__inner) {
  font-size: 1rem;
  line-height: 24px;
  height: 24px;
  color: #121212;
}

:deep(.el-select__placeholder),
:deep(.el-input__inner::placeholder) {
  color: rgba(0, 0, 0, .75);
}

.se-earnings-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding-right: 12px;
}

.se-earnings-title-block {
  flex: 1 1 auto;
  text-align: left;
  line-height: 1.3;
}

.se-panel-title {
  font-weight: 700;
  font-size: 1.15rem;
  margin-top: 10px;
  margin-bottom: 2px;
}

.se-panel-subtitle {
  font-weight: 400;
  color: rgba(0, 0, 0, .6);
  font-size: .875rem;
  margin-top: 0;
}

.se-paystub-summary-table {
  border-collapse: collapse;
  margin-top: 6px;
  font-size: .875rem;
  cursor: default;
}

.se-paystub-summary-table th,
.se-paystub-summary-table td {
  text-align: left;
  padding: 2px 16px 2px 0;
  font-weight: 400;
  color: rgba(0, 0, 0, .6);
  white-space: nowrap;
}

.se-paystub-summary-table th {
  font-weight: 600;
  color: rgba(0, 0, 0, .75);
}

.se-earnings-status {
  flex: 0 0 auto;
  font-weight: 600;
  margin-right: 12px;
}

.se-earnings-delete-btn {
  flex: 0 0 auto;
  margin-right: 16px;
  white-space: nowrap;
  padding: 2px 10px;
  font-size: .8rem;
  line-height: 1.4;
  min-height: auto;
}

.se-earnings-status--complete {
  color: #1a7d3a;
}

.se-earnings-status--incomplete {
  color: rgba(0, 0, 0, .5);
}

:deep(.el-collapse-item__header) {
  height: auto;
  min-height: 48px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.se-earnings-add-row {
  margin-top: 40px;
}
</style>

